import React, { useState, Suspense, useEffect, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stage, useGLTF, useTexture, Html, Line, useProgress } from '@react-three/drei';
import * as THREE from 'three';

// Workaround for missing intrinsic element types in some environments
const Group = 'group' as any;

// Appending query param to SHEEN_URL to ensure unique cache entry for WebGL
const SHEEN_URL = "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/Common_Sheen.webp?format=3d";

// A refined Loader component to show progress within the 3D scene
function Loader() {
  const { progress } = useProgress();
  return (
    <Html center>
      <div className="flex flex-col items-center justify-center gap-4 bg-white/95 backdrop-blur-md p-8 rounded-[2rem] border border-slate-100 shadow-2xl min-w-[240px]">
        <div className="relative w-14 h-14">
          <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
          <div className="absolute inset-0 border-4 border-[#FF5C00] rounded-full animate-spin border-t-transparent shadow-[0_0_15px_rgba(255,92,0,0.3)]"></div>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Loading Assets</p>
          <p className="text-xl font-black text-[#021226] tracking-tighter">
            {Math.round(progress)}%
          </p>
        </div>
      </div>
    </Html>
  );
}

// Appending version parameter to clear cache for updated models
const variants = [
  {
    id: '3seater',
    name: '3 Seater',
    url: "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/OpusDemo.glb?v=1.0.2",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full p-2" stroke="currentColor">
        <rect x="3" y="6" width="18" height="8" rx="1.5" strokeWidth="1.5" />
        <line x1="9" y1="6" x2="9" y2="14" strokeWidth="1.5" />
        <line x1="15" y1="6" x2="15" y2="14" strokeWidth="1.5" />
      </svg>
    )
  },
  {
    id: 'left',
    name: 'Chaise Left',
    url: "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/OpusDemo_LeftLounger.glb?v=1.0.2",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full p-2" stroke="currentColor">
        <path d="M4 6V18H8V10H20V6H4Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="8" y1="6" x2="8" y2="10" strokeWidth="1.5" />
        <line x1="14" y1="6" x2="14" y2="10" strokeWidth="1.5" />
      </svg>
    )
  },
  {
    id: 'right',
    name: 'Chaise Right',
    url: "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/OPusDemo_RightLounger.glb?v=1.0.2",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full p-2" stroke="currentColor">
        <path d="M20 6V18H16V10H4V6H20Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="16" y1="6" x2="16" y2="10" strokeWidth="1.5" />
        <line x1="10" y1="6" x2="10" y2="10" strokeWidth="1.5" />
      </svg>
    )
  },
  {
    id: 'corner',
    name: 'Sectional',
    url: "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/OPusDemo_Corner.glb?v=1.0.2",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-full h-full p-2" stroke="currentColor">
        <path d="M4 20V10C4 6.68629 6.68629 4 10 4H20V8H10C8.89543 8 8 8.89543 8 10V20H4Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="12" y1="4" x2="12" y2="8" strokeWidth="1.5" />
        <line x1="16" y1="4" x2="16" y2="8" strokeWidth="1.5" />
      </svg>
    )
  }
];

const fabrics = [
  {
    id: 'maroon',
    name: 'Royal Maroon',
    texture: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/Maroon.webp',
  },
  {
    id: 'teal',
    name: 'Ocean Teal',
    texture: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/Teal.webp',
  },
  {
    id: 'orange',
    name: 'Sunset Orange',
    texture: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/Orange.webp',
  },
  {
    id: 'mustard',
    name: 'Honey Mustard',
    texture: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/Mustard.webp',
  }
];

const woodFinishes = [
  {
    id: 'sheesham',
    name: 'Sheesham',
    texture: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/Sheesham.webp'
  },
  {
    id: 'teak',
    name: 'Teak',
    texture: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/Teak.webp'
  },
  {
    id: 'walnut',
    name: 'Walnut',
    texture: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/Walnut.webp'
  },
  {
    id: 'wenge',
    name: 'Wenge',
    texture: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/MiniConfigurator/Wenge.webp'
  }
];

const configurableParts = [
  { id: 'body', label: 'Body' },
  { id: 'pillow', label: 'Pillow' },
  { id: 'wood', label: 'Wood' }
];

// Component to render 3D dimensions lines and labels
function DimensionsOverlay({ bounds }: { bounds: THREE.Box3 }) {
  if (bounds.isEmpty()) return null;

  const { min, max } = bounds;
  const size = new THREE.Vector3();
  bounds.getSize(size);

  // Convert roughly to cm for display (assuming model unit scale)
  const width = Math.round(size.x * 100);
  const height = Math.round(size.y * 100);
  const depth = Math.round(size.z * 100);

  const lineProps = { color: "#FF5C00", lineWidth: 1.5, dashed: false };
  const labelStyle = "bg-[#021226]/90 backdrop-blur text-white text-[10px] px-2 py-0.5 rounded-md font-mono font-bold whitespace-nowrap border border-white/20";

  return (
    <Group>
      {/* Height Line (Right side) */}
      <Line points={[[max.x, min.y, 0], [max.x, max.y, 0]]} {...lineProps} />
      <Html position={[max.x, (min.y + max.y) / 2, 0]} center>
        <div className={labelStyle} style={{ marginLeft: '10px' }}>H: {height}</div>
      </Html>

      {/* Width Line (Bottom) */}
      <Line points={[[min.x, min.y, max.z], [max.x, min.y, max.z]]} {...lineProps} />
      <Html position={[(min.x + max.x) / 2, min.y, max.z]} center>
        <div className={labelStyle} style={{ marginTop: '10px' }}>W: {width}</div>
      </Html>

      {/* Depth Line (Left) */}
      <Line points={[[min.x, min.y, min.z], [min.x, min.y, max.z]]} {...lineProps} />
      <Html position={[min.x, min.y, (min.z + max.z) / 2]} center>
        <div className={labelStyle} style={{ marginRight: '10px', transform: 'translateX(-100%)' }}>D: {depth}</div>
      </Html>
    </Group>
  );
}

// Props type definition
type ChairModelProps = {
  modelUrl: string;
  bodyTextureUrl: string;
  pillowTextureUrl: string;
  woodTextureUrl: string;
  showDimensions: boolean;
  key?: React.Key;
  [key: string]: any
};

function ChairModel({ modelUrl, bodyTextureUrl, pillowTextureUrl, woodTextureUrl, showDimensions, ...props }: ChairModelProps) {
  const { scene } = useGLTF(modelUrl);

  // Use cache busting for 3D textures to avoid CORS issues
  const texturesToLoad = useMemo(() => [
    bodyTextureUrl + "?format=3d",
    pillowTextureUrl + "?format=3d",
    woodTextureUrl + "?format=3d",
    SHEEN_URL
  ], [bodyTextureUrl, pillowTextureUrl, woodTextureUrl]);

  const [bodyTex, pillowTex, woodTex, sheenTex] = useTexture(texturesToLoad);
  const [bounds, setBounds] = useState(new THREE.Box3());

  // Configure textures
  [bodyTex, pillowTex, woodTex, sheenTex].forEach(t => {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(16, 16);
    t.colorSpace = THREE.SRGBColorSpace;
    t.flipY = false;
  });

  // Adjust wood repeat if necessary (often needs to be smaller for wood grain)
  woodTex.repeat.set(4, 4);

  const clonedScene = useMemo(() => scene.clone(), [scene]);

  // Calculate bounds
  useEffect(() => {
    if (clonedScene) {
      const box = new THREE.Box3().setFromObject(clonedScene);
      setBounds(box);
    }
  }, [clonedScene]);

  // Apply materials based on part names
  useEffect(() => {
    clonedScene.traverse((child: any) => {
      if ((child as any).isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;

        const nameLower = child.name.toLowerCase();

        // 1. Identify Wood Parts (Starts with 'wood')
        if (nameLower.startsWith('wood')) {
          child.material = new THREE.MeshPhysicalMaterial({
            map: woodTex,
            color: 'white',
            roughness: 0.6,
            metalness: 0.0,
          });
        }
        // 2. Identify Pillows
        else if (nameLower.includes('pillow') || nameLower.includes('cushion')) {
          child.material = new THREE.MeshPhysicalMaterial({
            map: pillowTex,
            color: 'white',
            roughness: 0.8,
            metalness: 0.0,
            sheen: 1.0,
            sheenRoughness: 0.9,
            sheenColorMap: sheenTex,
          });
        }
        // 3. Identify Body (Everything else not specifically excluded)
        else {
          const isExcluded = nameLower.includes('leg') ||
            nameLower.includes('feet') ||
            nameLower.includes('metal') ||
            nameLower.includes('frame');

          if (!isExcluded) {
            child.material = new THREE.MeshPhysicalMaterial({
              map: bodyTex,
              color: 'white',
              roughness: 0.8,
              metalness: 0.0,
              sheen: 1.0,
              sheenRoughness: 0.9,
              sheenColorMap: sheenTex,
            });
          }
        }
      }
    });
  }, [clonedScene, bodyTex, pillowTex, woodTex, sheenTex]);

  const Primitive = 'primitive' as any;

  return (
    <Group>
      <Primitive object={clonedScene} {...props} />
      {showDimensions && <DimensionsOverlay bounds={bounds} />}
    </Group>
  );
}

// NOTE: Preloading removed to support click-to-load optimization. 
// Assets will load when the user interacts with the placeholder.

interface MiniConfiguratorProps {
  onBookDemo?: () => void;
}

const MiniConfigurator: React.FC<MiniConfiguratorProps> = ({ onBookDemo }) => {
  const [activeVariantId, setActiveVariantId] = useState(variants[0].id);
  const [showDimensions, setShowDimensions] = useState(false);
  const [is3DLoaded, setIs3DLoaded] = useState(false);

  // New State for independent part selection
  const [activePart, setActivePart] = useState<'body' | 'pillow' | 'wood'>('body');
  const [selections, setSelections] = useState({
    body: fabrics[0].id,
    pillow: fabrics[0].id,
    wood: woodFinishes[0].id
  });

  const activeVariant = variants.find(v => v.id === activeVariantId)!;

  // Helper to get current texture URL based on selection
  const getTextureUrl = (part: 'body' | 'pillow' | 'wood') => {
    if (part === 'wood') {
      return woodFinishes.find(f => f.id === selections.wood)!.texture;
    }
    return fabrics.find(f => f.id === selections[part])!.texture;
  };

  const handleMaterialSelect = (materialId: string) => {
    setSelections(prev => ({
      ...prev,
      [activePart]: materialId
    }));
  };

  const handleAR = () => {
    if ('xr' in navigator && (navigator as any).xr) {
      alert("Launching AR Viewer... (Note: Requires a WebXR compatible device)");
    } else {
      alert("AR Quick Look requires a mobile device or WebXR compatible browser.");
    }
  };

  const scrollToProducts = () => {
    const element = document.getElementById('products');
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  // Determine which list of materials to show
  const currentMaterialList = activePart === 'wood' ? woodFinishes : fabrics;
  const currentSelectionId = selections[activePart];

  return (
    <section id="demo" className="pt-10 pb-12 md:pt-16 md:pb-16 bg-[#F0F4F8] relative overflow-hidden">
      {/* Top Fade */}
      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none"></div>

      {/* Distinct Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>

      <div className="container-xl relative z-10">
        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-8 lg:gap-x-16 xl:gap-x-20 items-center">

          {/* Left Column Wrapper: Header + Controls - Desktop only logic */}
          <div className="contents lg:flex lg:flex-col lg:col-span-5 lg:gap-8 lg:justify-center">

            {/* Header Text - Order 1 on Mobile */}
            <div className="w-full order-1 lg:order-none">
              <span className="inline-block px-3 py-1 md:px-4 md:py-1.5 rounded-full bg-blue-50 text-[#003B5C] text-[9px] md:text-[10px] font-bold tracking-[0.2em] uppercase mb-4 md:mb-8 border border-blue-100 shadow-sm">
                Live Interactive Prototype
              </span>
              <h2 data-animation="fade-in-up" className="text-2xl md:text-4xl font-bold text-[#021226] leading-tight tracking-tight mb-4 md:mb-6">
                The Engine <br />
                <span className="text-[#FF5C00]">Behind Reality</span>
              </h2>
              <p data-animation="fade-in-up" className="text-sm md:text-lg text-slate-600 font-normal leading-relaxed mb-6 md:mb-4 stagger-1">
                Test our core visualization engine. Switch fabric, customize parts, all in real-time.
              </p>
            </div>

            {/* Controls - Order 3 on Mobile - COMPACT UNIFIED CARD DESIGN */}
            <div className="w-full order-3 lg:order-none">
              <div data-animation="fade-in-up" className="bg-white rounded-[2rem] p-6 md:p-8 shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col gap-6 stagger-2 relative overflow-hidden">
                {/* Decorative accent */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                {/* Section 1: Layouts */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Select Layout</p>
                    <span className="text-[9px] font-bold text-[#021226]">{activeVariant.name}</span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar snap-x">
                    {variants.map((v) => (
                      <button
                        key={v.id}
                        onClick={() => setActiveVariantId(v.id)}
                        className={`flex-shrink-0 snap-start relative w-12 h-12 md:w-14 md:h-14 rounded-xl transition-all duration-300 transform flex items-center justify-center border ${activeVariantId === v.id
                          ? 'border-[#003B5C] text-[#003B5C] bg-[#f1f5f9] ring-2 ring-[#003B5C]/10'
                          : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300 hover:text-slate-600'
                          }`}
                        title={v.name}
                      >
                        <div className="scale-100">{v.icon}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="h-px w-full bg-slate-100"></div>

                {/* Section 2: Finishes (Segmented Control + Grid) */}
                <div>
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 mb-3">Customize Finish</p>

                  {/* Segmented Part Selector */}
                  <div className="flex p-1 bg-slate-100 rounded-xl mb-4 relative z-10">
                    {configurableParts.map((part) => (
                      <button
                        key={part.id}
                        onClick={() => setActivePart(part.id as any)}
                        className={`flex-1 py-2 rounded-lg text-[9px] font-bold uppercase tracking-wider transition-all duration-300 ${activePart === part.id
                          ? 'bg-[#003B5C] text-white shadow-md'
                          : 'text-slate-400 hover:text-slate-600'
                          }`}
                      >
                        {part.label}
                      </button>
                    ))}
                  </div>

                  {/* Materials Grid */}
                  <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                    {currentMaterialList.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => handleMaterialSelect(m.id)}
                        className={`relative w-10 h-10 md:w-12 md:h-12 rounded-full transition-all duration-300 transform shadow-sm overflow-hidden group ${currentSelectionId === m.id
                          ? 'ring-4 ring-offset-2 ring-[#003B5C] scale-105'
                          : 'hover:scale-110 opacity-90 hover:opacity-100 ring-1 ring-slate-100'
                          }`}
                        style={{
                          backgroundImage: `url(${m.texture})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                        title={m.name}
                      >
                        {currentSelectionId === m.id && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <svg className="w-4 h-4 text-white drop-shadow-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                        <span className="sr-only">{m.name}</span>
                      </button>
                    ))}
                  </div>
                  <p className="mt-3 text-[10px] font-bold text-center md:text-left text-slate-500">
                    <span className="text-slate-300 font-medium">Selected:</span> {currentMaterialList.find(m => m.id === currentSelectionId)?.name}
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* 3D Canvas - Order 2 on Mobile (Bigger), Col Span 7 on Desktop */}
          <div className="w-full order-2 lg:order-none lg:col-span-7">
            <div data-animation="zoom-in" className="relative group">
              {/* Background Glow */}
              <div className="absolute -inset-4 bg-gradient-to-tr from-blue-100/50 to-orange-100/50 rounded-[3rem] blur-2xl opacity-70"></div>

              <div className="relative bg-white rounded-[2rem] md:rounded-[3rem] p-2 md:p-3 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.1)] md:shadow-[0_40px_100px_-15px_rgba(0,0,0,0.1)] border border-slate-100 overflow-hidden aspect-square md:aspect-[4/3]">
                {/* Canvas Container */}
                <div className="relative w-full h-full rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 cursor-grab active:cursor-grabbing touch-none">

                  {!is3DLoaded ? (
                    <div
                      className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 cursor-pointer group/placeholder z-20"
                      onClick={() => setIs3DLoaded(true)}
                    >
                      {/* Static Image Background Placeholder */}
                      <div className="absolute inset-0 bg-[url('https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/Hero/HomePage-1-1.webp')] bg-cover bg-center opacity-40 blur-[2px] transition-all duration-500 group-hover/placeholder:opacity-50 group-hover/placeholder:blur-0 transform group-hover/placeholder:scale-105"></div>
                      <div className="absolute inset-0 bg-gradient-to-t from-[#021226]/80 via-[#021226]/20 to-transparent"></div>

                      {/* CTA Button */}
                      <div className="relative z-10 flex flex-col items-center gap-4 animate-reveal">
                        <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(255,92,0,0.3)] border border-white/20 group-hover/placeholder:scale-110 group-hover/placeholder:bg-white group-hover/placeholder:text-[#FF5C00] text-white transition-all duration-500">
                          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zm0 9l2-1-2-1-2 1 2 1zm0-3.5L6 7l6-2.5L18 7l-6 2.5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
                        </div>
                        <div className="text-center">
                          <h3 className="text-2xl font-black text-white tracking-tight mb-1 drop-shadow-lg">Load 3D Experience</h3>
                          <p className="text-xs font-bold text-slate-300 uppercase tracking-widest drop-shadow-md">Interactive Real-time Engine</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Overlay UI Controls */}
                      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-50 flex flex-col md:flex-row gap-2 pointer-events-auto items-end md:items-start animate-reveal">
                        <button
                          onClick={() => setShowDimensions(!showDimensions)}
                          className={`flex items-center justify-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all shadow-lg border ${showDimensions
                            ? 'bg-[#FF5C00] text-white border-[#FF5C00]'
                            : 'bg-white/90 backdrop-blur text-[#021226] border-white/40 hover:bg-white'
                            }`}
                          aria-label="Toggle Dimensions"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                          <span className="hidden md:inline">Dimensions</span>
                        </button>

                        <button
                          onClick={handleAR}
                          className="flex items-center justify-center gap-2 px-3 py-2 md:px-4 md:py-2 bg-[#021226] text-white rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest hover:bg-[#003B5C] transition-all shadow-lg border border-white/10"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" /></svg>
                          <span className="md:hidden">AR</span>
                          <span className="hidden md:inline">View in AR</span>
                        </button>
                      </div>

                      <Canvas shadows dpr={[1, 2]} camera={{ fov: 45 }}>
                        <Suspense fallback={<Loader />}>
                          <Stage environment="city" intensity={0.5} shadows={{ type: 'contact', opacity: 0.4, blur: 2 }}>
                            <ChairModel
                              key={activeVariant.url} // Force remount when variant changes
                              modelUrl={activeVariant.url}
                              bodyTextureUrl={getTextureUrl('body')}
                              pillowTextureUrl={getTextureUrl('pillow')}
                              woodTextureUrl={getTextureUrl('wood')}
                              showDimensions={showDimensions}
                            />
                          </Stage>
                        </Suspense>
                        <OrbitControls
                          enableZoom={true}
                          enablePan={false}
                          minPolarAngle={Math.PI / 4}
                          maxPolarAngle={Math.PI / 2}
                          autoRotate={!showDimensions}
                          autoRotateSpeed={0.5}
                        />
                      </Canvas>

                      <div className="absolute bottom-6 left-6 pointer-events-none max-w-[200px] animate-reveal">
                        <div className="p-3.5 glass rounded-2xl border border-white/40 shadow-xl backdrop-blur-md">
                          <p className="text-[9px] font-mono font-bold text-[#FF5C00] uppercase tracking-widest mb-1">Interactive</p>
                          <p className="text-[10px] font-bold text-[#021226] flex items-center gap-2 tracking-tight">
                            Drag to Rotate & Explore
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CONFIGURATOR ENDS HERE - CTA MOVED TO SUCCESSBRIDGE */}

        </div>
      </div>
    </section>
  );
};

export default MiniConfigurator;